import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Snackbar, Alert, Slide, Button, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { authFetch } from '../utils/api';
import { buildLiveDeepLink } from '../utils/deepLinks';
import { disposeWebSocket } from '../utils/disposeWebSocket';

const WS_URL = process.env.REACT_APP_API_WEBSOCKET_URL;
const API = process.env.REACT_APP_API_BASE_URL;

// App-wide context that exposes:
//   registerJoinedSession(sessionId)   — call when the agent joins a session
//   unregisterJoinedSession(sessionId) — call when the agent leaves
// LiveInbox uses these so the provider knows which sessions should trigger
// "new visitor message" toasts. Other parts of the app can ignore the context.
const LiveNotificationsContext = createContext({
  registerJoinedSession: () => {},
  unregisterJoinedSession: () => {},
  // P7 — agent-online heartbeat: LiveInbox tells the provider which bots are
  // currently toggled online. Provider fires POST /live/agent_heartbeat every
  // 30s for each online bot so the server doesn't reap us to offline.
  setAgentOnline: () => {},
});

export const useLiveNotifications = () => useContext(LiveNotificationsContext);

const SlideLeft = (props) => <Slide {...props} direction="left" />;

/**
 * App-wide provider that:
 *   - opens ONE WebSocket per logged-in tab
 *   - subscribes to live events for every bot the user owns
 *   - shows top-right MUI Snackbar toasts for two events:
 *       * live_session_created  → "New visitor on {bot}"
 *       * live_visitor_message  → only if the agent is currently joined
 *
 * Toasts are queued (one visible at a time) and clicking the toast navigates
 * to the Live tab and pre-selects the relevant bot via localStorage.
 */
const LiveNotificationsProvider = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Bots owned by the user (id + name). Loaded once on mount; refreshed if the
  // WS reports a new session for an unknown bot (covers the create-bot case
  // without needing a separate WS event).
  const [ownedBots, setOwnedBots] = useState([]);
  const ownedBotsRef = useRef([]);
  useEffect(() => { ownedBotsRef.current = ownedBots; }, [ownedBots]);

  // Sessions the agent has joined (set in LiveInbox via context).
  const joinedSessionsRef = useRef(new Set());

  // Most recent `new_chat` toast key per session_id. If a `help_requested`
  // arrives shortly after for the same session (the visitor immediately
  // clicked "Speak to an agent" on an empty chat), we dismiss the now-
  // redundant new_chat toast and only keep the more urgent one. This keeps
  // new_chat toasts instant in the normal case while still de-duplicating
  // the empty-chat → request-agent flow.
  const newChatToastKeyBySessionRef = useRef(new Map());

  // Per-bot in-app notification preferences. Keyed by bot_id; each entry is
  // { new_chat: bool, help_requested: bool, new_message: bool }. Hydrated
  // from /notification_settings/{bot_id} when the owned-bot set changes.
  // Defaults are applied if a bot has no row yet (preserves legacy behavior).
  const inAppPrefsRef = useRef(new Map());

  const DEFAULT_IN_APP_PREFS = useMemo(() => ({
    new_chat: true,
    help_requested: true,
    new_message: true,
    usage_warning: true,
  }), []);

  const isInAppEnabled = useCallback((botId, eventKey) => {
    if (!botId) return true;
    const prefs = inAppPrefsRef.current.get(botId);
    if (!prefs) return DEFAULT_IN_APP_PREFS[eventKey] !== false;
    const val = prefs[eventKey];
    return typeof val === 'boolean' ? val : DEFAULT_IN_APP_PREFS[eventKey] !== false;
  }, [DEFAULT_IN_APP_PREFS]);

  // WS + reconnection
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  // P1 — WS health for polling demotion
  const wsHealthRef = useRef({ lastEventAt: 0, connected: false });
  // P5 — exp backoff
  const attemptRef = useRef(0);
  // P6 — terminal auth failure (4401) flag
  const terminalAuthFailureRef = useRef(false);
  // P4 — track which bot_ids we've sent live_subscribe for
  const subscribedBotIdsRef = useRef([]);
  const pendingSubsRef = useRef({ add: new Set(), remove: new Set() });
  // P3 — joined-sessions reconcile interval
  const reconcileTimerRef = useRef(null);
  // P7 — agent-online heartbeat state
  const onlineBotsRef = useRef(new Set());
  const heartbeatTimerRef = useRef(null);
  // P9 — playPing throttle
  const lastPingAtRef = useRef(0);

  // Active toasts (up to MAX_VISIBLE shown at once, stacked vertically).
  // Oldest toasts are dropped when the cap is exceeded so newer events
  // don't get queued behind a backlog.
  const MAX_VISIBLE = 3;
  const TOAST_AUTO_HIDE_MS = 6000;
  // Safety dismiss = autoHide + a small grace period. We use this as a
  // wall-clock fallback in case MUI's internal Snackbar timer is paused
  // (it pauses on hover, on window blur, and when the tab is hidden), which
  // can otherwise leave a toast on screen indefinitely.
  const TOAST_SAFETY_DISMISS_MS = 10000;
  const [toasts, setToasts] = useState([]);
  // Per-toast safety timers, keyed by toast.key. Cleared on manual dismiss.
  const toastSafetyTimersRef = useRef(new Map());

  // Lazily-created shared AudioContext for the notification ping. Created on
  // first use so the browser ties it to a user activation when possible.
  // Falls back silently if the browser blocks playback (e.g. tab restored
  // before any user interaction — autoplay policy will reject .resume()).
  const audioCtxRef = useRef(null);
  const playPing = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      // Resume if the context was suspended by autoplay policy.
      if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
        ctx.resume().catch(() => { /* blocked, ignore */ });
      }
      const now = ctx.currentTime;
      // Two-tone ping: 880 Hz then 1320 Hz, ~180ms total, gentle envelope.
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.6, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
      gain.connect(ctx.destination);

      const osc1 = ctx.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now);
      osc1.frequency.setValueAtTime(1320, now + 0.09);
      osc1.connect(gain);
      osc1.start(now);
      osc1.stop(now + 0.24);
    } catch {
      // Audio not supported or blocked — silently ignore.
    }
  }, []);

  // Mark a toast as exiting so the Snackbar plays its slide-out transition.
  // The toast is removed from the array only after the transition finishes
  // (see Snackbar's TransitionProps.onExited below).
  const dismissToast = useCallback((key) => {
    setToasts((prev) => prev.map((tst) => (
      tst.key === key ? { ...tst, exiting: true } : tst
    )));
    const timer = toastSafetyTimersRef.current.get(key);
    if (timer) {
      clearTimeout(timer);
      toastSafetyTimersRef.current.delete(key);
    }
  }, []);

  // Final removal once the slide-out transition has played.
  const removeToast = useCallback((key) => {
    setToasts((prev) => prev.filter((tst) => tst.key !== key));
  }, []);

  const enqueueToast = useCallback((toast) => {
    const key = Date.now() + Math.random();
    setToasts((prev) => {
      const next = [...prev, { ...toast, key, exiting: false }];
      // Drop oldest if we're over the visible cap. Also clear any pending
      // safety timers for the dropped toasts so we don't leak setTimeouts.
      if (next.length > MAX_VISIBLE) {
        const dropped = next.slice(0, next.length - MAX_VISIBLE);
        for (const d of dropped) {
          const timer = toastSafetyTimersRef.current.get(d.key);
          if (timer) {
            clearTimeout(timer);
            toastSafetyTimersRef.current.delete(d.key);
          }
        }
        return next.slice(next.length - MAX_VISIBLE);
      }
      return next;
    });
    // Wall-clock safety dismiss — fires even if MUI's internal autoHide is
    // paused (hover / blur / hidden tab). Trigger the same exit animation.
    const safetyTimer = setTimeout(() => {
      toastSafetyTimersRef.current.delete(key);
      setToasts((prev) => prev.map((tst) => (
        tst.key === key ? { ...tst, exiting: true } : tst
      )));
    }, TOAST_SAFETY_DISMISS_MS);
    toastSafetyTimersRef.current.set(key, safetyTimer);
    // P9 — coalesce: cap one ping per 500ms so bursts of toasts don't stack audio
    const now = Date.now();
    if (now - (lastPingAtRef.current || 0) >= 500) {
      lastPingAtRef.current = now;
      playPing();
    }
    return key;
  }, [playPing]);

  const fetchOwnedBots = useCallback(async () => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return [];
    try {
      const res = await fetch(
        `${API}bots?user_id=${userId}&include_knowledge=False`
      );
      if (!res.ok) return [];
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const minimal = list.map((b) => ({ id: b.id, name: b.name || 'Bot' }));
      setOwnedBots(minimal);
      return minimal;
    } catch (err) {
      console.error('LiveNotifications: fetchOwnedBots error', err);
      return [];
    }
  }, []);

  const getBotName = useCallback((botId) => {
    const found = ownedBotsRef.current.find((b) => b.id === botId);
    return found?.name || t('live_notify_bot_fallback') || 'your bot';
  }, [t]);

  const registerJoinedSession = useCallback((sessionId) => {
    if (sessionId) joinedSessionsRef.current.add(sessionId);
  }, []);
  const unregisterJoinedSession = useCallback((sessionId) => {
    if (sessionId) joinedSessionsRef.current.delete(sessionId);
  }, []);

  const handleEvent = useCallback((data) => {
    if (!data || !data.type) return;

    // Diagnostic logging — log EVERY incoming WS event so we can see if
    // live_visitor_message is arriving at all.
    // eslint-disable-next-line no-console
    console.debug('[LiveNotifications] event', data.type, {
      session_id: data.session_id || data.session?.session_id,
      bot_id: data.bot_id,
      agent_user_id: data.session?.agent_user_id,
      joined_set: Array.from(joinedSessionsRef.current),
    });

    // Authoritative tracking of joined sessions via backend events. We
    // can't rely on LiveInbox alone because it unmounts when the agent
    // navigates away from /live — at which point we'd lose the joined-set
    // and stop toasting visitor messages.
    const myUserId = localStorage.getItem('user_id');
    if (data.type === 'live_agent_joined') {
      const sid = data.session_id || data.session?.session_id;
      const agentUid = data.session?.agent_user_id;
      if (sid && myUserId && String(agentUid) === String(myUserId)) {
        joinedSessionsRef.current.add(sid);
      }
    } else if (data.type === 'live_agent_left' || data.type === 'live_session_ended') {
      const sid = data.session_id || data.session?.session_id;
      if (sid) joinedSessionsRef.current.delete(sid);
    }

    if (data.type === 'live_session_created') {
      // Skip if the agent is already inside that session (no point toasting).
      const sid = data.session?.session_id;
      if (sid && joinedSessionsRef.current.has(sid)) return;
      if (!isInAppEnabled(data.bot_id, 'new_chat')) return;

      // If the bot is unknown (e.g. created since last fetch), refresh.
      if (data.bot_id && !ownedBotsRef.current.some((b) => b.id === data.bot_id)) {
        fetchOwnedBots();
      }

      const botName = getBotName(data.bot_id);
      const visitor = data.session?.visitor_name
        || (t('live_notify_visitor_fallback') || 'A visitor');
      const toastKey = enqueueToast({
        severity: 'info',
        title: t('live_notify_new_session_title') || 'New live conversation',
        body: (t('live_notify_new_session_body')
          || '{{visitor}} just started a chat on {{bot}}.')
          .replace('{{visitor}}', visitor)
          .replace('{{bot}}', botName),
        botId: data.bot_id,
        sessionId: sid,
      });
      // Remember which toast belongs to this session so a follow-up
      // help_requested can dismiss it.
      if (sid && toastKey != null) {
        newChatToastKeyBySessionRef.current.set(sid, toastKey);
      }
      return;
    }

    if (data.type === 'live_agent_requested') {
      const sid = data.session?.session_id;
      // Dismiss the new_chat toast for this session if it's still on screen —
      // the user just asked for a human, so the help_requested toast should
      // be the only one shown.
      if (sid) {
        const newChatKey = newChatToastKeyBySessionRef.current.get(sid);
        if (newChatKey != null) {
          dismissToast(newChatKey);
          newChatToastKeyBySessionRef.current.delete(sid);
        }
      }
      // Skip if the agent is already inside that session.
      if (sid && joinedSessionsRef.current.has(sid)) return;
      if (!isInAppEnabled(data.bot_id, 'help_requested')) return;

      if (data.bot_id && !ownedBotsRef.current.some((b) => b.id === data.bot_id)) {
        fetchOwnedBots();
      }

      const botName = getBotName(data.bot_id);
      const visitor = data.session?.visitor_name
        || (t('live_notify_visitor_fallback') || 'A visitor');
      enqueueToast({
        severity: 'warning',
        title: t('live_notify_agent_requested_title') || 'Live agent requested',
        body: (t('live_notify_agent_requested_body')
          || '{{visitor}} is asking to talk to a human on {{bot}}.')
          .replace('{{visitor}}', visitor)
          .replace('{{bot}}', botName),
        botId: data.bot_id,
        sessionId: sid,
      });
      return;
    }

    if (data.type === 'live_visitor_message') {
      const sid = data.session_id;
      if (!sid || !joinedSessionsRef.current.has(sid)) return;
      if (!isInAppEnabled(data.bot_id, 'new_message')) return;
      const preview = (data.content || '').toString().slice(0, 80);
      enqueueToast({
        severity: 'success',
        title: t('live_notify_new_message_title') || 'New visitor message',
        body: preview,
        botId: data.bot_id,
        sessionId: sid,
      });
      return;
    }

    if (data.type === 'live_lead_created') {
      if (!isInAppEnabled(data.bot_id, 'new_lead')) return;
      if (data.bot_id && !ownedBotsRef.current.some((b) => b.id === data.bot_id)) {
        fetchOwnedBots();
      }
      const botName = getBotName(data.bot_id);
      const visitor = data.visitor_name
        || (t('live_notify_visitor_fallback') || 'A visitor');
      enqueueToast({
        severity: 'success',
        title: t('live_notify_new_lead_title') || 'New lead generated',
        body: (t('live_notify_new_lead_body')
          || '{{visitor}} just became a lead on {{bot}}.')
          .replace('{{visitor}}', visitor)
          .replace('{{bot}}', botName),
        botId: data.bot_id,
        sessionId: data.session_id,
      });
      return;
    }

    if (data.type === 'usage_warning') {
      if (!isInAppEnabled(data.bot_id, 'usage_warning')) return;
      const botName = data.bot_name || getBotName(data.bot_id);
      enqueueToast({
        severity: 'warning',
        title: t('live_notify_usage_warning_title') || 'Usage alert',
        body: (t('live_notify_usage_warning_body')
          || '{{bot}} has used {{percent}}% of its monthly AI responses.')
          .replace('{{bot}}', botName)
          .replace('{{percent}}', String(data.percent ?? '')),
        botId: data.bot_id,
        href: `/billing/${data.bot_id}`,
      });
      return;
    }

    // Monthly allowance exhausted (fires once per billing window, backend-
    // deduped). Same usage section prefs as usage_warning; more urgent copy.
    if (data.type === 'limit_reached') {
      if (!isInAppEnabled(data.bot_id, 'usage_warning')) return;
      const botName = data.bot_name || getBotName(data.bot_id);
      enqueueToast({
        severity: 'error',
        title: t('live_notify_limit_reached_title') || 'Message limit reached',
        body: (t('live_notify_limit_reached_body')
          || '{{bot}} has used all of its monthly AI responses and will pause. Upgrade or add responses to keep it answering.')
          .replace('{{bot}}', botName),
        botId: data.bot_id,
        href: `/billing/${data.bot_id}`,
      });
      return;
    }
  }, [enqueueToast, dismissToast, fetchOwnedBots, getBotName, t, isInAppEnabled]);

  // Open WS once owned bots are known. Re-runs only when the set of bot IDs
  // actually changes (sorted+joined as a stable key).
  const ownedBotIdsKey = useMemo(
    () => ownedBots.map((b) => b.id).sort().join('|'),
    [ownedBots]
  );

  // P2 + P3 — batch sessions fetcher shared by hydrate + periodic reconcile.
  // GET /live/sessions?bot_ids=a,b,c returns { "<bot_id>": [session, ...], ... }.
  // Cap 50 ids per call → chunk.
  const fetchAllJoinedSessions = useCallback(async () => {
    const myUserId = localStorage.getItem('user_id');
    if (!myUserId || !ownedBotIdsKey) return null;
    const botIds = ownedBotIdsKey.split('|').filter(Boolean);
    if (botIds.length === 0) return null;
    const CHUNK = 50;
    const chunks = [];
    for (let i = 0; i < botIds.length; i += CHUNK) {
      chunks.push(botIds.slice(i, i + CHUNK));
    }
    try {
      const results = await Promise.all(chunks.map(async (ids) => {
        try {
          const qs = encodeURIComponent(ids.join(','));
          const res = await authFetch(`${API}live/sessions?bot_ids=${qs}`);
          if (!res || !res.ok) return {};
          return await res.json();
        } catch {
          return {};
        }
      }));
      const all = [];
      for (const mapByBot of results) {
        if (!mapByBot || typeof mapByBot !== 'object') continue;
        for (const botId of Object.keys(mapByBot)) {
          const arr = mapByBot[botId];
          if (!Array.isArray(arr)) continue;
          for (const s of arr) all.push(s);
        }
      }
      return { myUserId, all };
    } catch (err) {
      console.error('LiveNotifications: batch sessions fetch error', err);
      return null;
    }
  }, [ownedBotIdsKey]);

  // Rebuild joinedSessionsRef from a session list (P3).
  const reconcileJoinedSessions = useCallback(async () => {
    const result = await fetchAllJoinedSessions();
    if (!result) return;
    const { myUserId, all } = result;
    const next = new Set(
      all
        .filter((s) => s.status === 'agent_joined' && String(s.agent_user_id) === String(myUserId))
        .map((s) => s.session_id)
    );
    joinedSessionsRef.current = next;
  }, [fetchAllJoinedSessions]);

  // Keep a ref to reconcileJoinedSessions so the WS lifecycle effect (which
  // doesn't re-run on owned-bot changes) always calls the latest version.
  const reconcileJoinedSessionsRef = useRef(reconcileJoinedSessions);
  useEffect(() => {
    reconcileJoinedSessionsRef.current = reconcileJoinedSessions;
  }, [reconcileJoinedSessions]);

  // Keep a ref to the latest ownedBotIdsKey for the WS effect's onopen closure.
  const ownedBotIdsKeyRef = useRef('');
  useEffect(() => {
    ownedBotIdsKeyRef.current = ownedBotIdsKey || '';
  }, [ownedBotIdsKey]);

  // WS lifecycle (P4: decoupled from ownedBotIdsKey so filter changes don't
  // tear down the socket — Effect B below diffs the subscription set).
  // P5: exp backoff with ±50% jitter. P6: init carries JWT + handles auth_error.
  const userIdForWs = localStorage.getItem('user_id');
  useEffect(() => {
    if (!userIdForWs) return;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      // P6 — never open WS without a token
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        console.warn('LiveNotifications WS: no auth token available, skipping connect');
        return;
      }
      if (terminalAuthFailureRef.current) {
        console.warn('LiveNotifications WS: prior 4401 close, not reconnecting');
        return;
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try { wsRef.current.close(); } catch { /* noop */ }
      }
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled || wsRef.current !== ws) { disposeWebSocket(ws); return; }
        wsHealthRef.current.connected = true;
        wsHealthRef.current.lastEventAt = Date.now();
        attemptRef.current = 0;
        try {
          // P6 — init REQUIRES the JWT
          ws.send(JSON.stringify({ type: 'init', user_id: userIdForWs, token: authToken }));
          // P4 — subscribe to current owned bots; contract is additive
          const key = ownedBotIdsKeyRef.current || '';
          const ids = key ? key.split('|').filter(Boolean) : [];
          if (ids.length > 0) {
            ws.send(JSON.stringify({ type: 'live_subscribe', bot_ids: ids }));
          }
          subscribedBotIdsRef.current = ids;
          // Flush any queued subscribe/unsubscribe deltas
          const pendingAdd = Array.from(pendingSubsRef.current.add);
          const pendingRemove = Array.from(pendingSubsRef.current.remove);
          if (pendingAdd.length > 0) {
            const extra = pendingAdd.filter((b) => !ids.includes(b));
            if (extra.length > 0) {
              ws.send(JSON.stringify({ type: 'live_subscribe', bot_ids: extra }));
              subscribedBotIdsRef.current = [...subscribedBotIdsRef.current, ...extra];
            }
          }
          if (pendingRemove.length > 0) {
            ws.send(JSON.stringify({ type: 'live_unsubscribe', bot_ids: pendingRemove }));
            subscribedBotIdsRef.current = subscribedBotIdsRef.current.filter(
              (b) => !pendingRemove.includes(b)
            );
          }
          pendingSubsRef.current = { add: new Set(), remove: new Set() };
        } catch (err) {
          console.error('LiveNotifications WS open send error:', err);
        }
        // P1/P3 — catch-up reconcile on (re)connect. Piggybacks the batch fetch.
        reconcileJoinedSessionsRef.current?.();
      };

      ws.onmessage = (event) => {
        if (cancelled || wsRef.current !== ws) return;
        wsHealthRef.current.lastEventAt = Date.now();
        wsHealthRef.current.connected = true;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }
          if (data.type === 'auth_error') {
            console.error('LiveNotifications WS auth_error:', data.reason || '(no reason)');
            terminalAuthFailureRef.current = true;
            try { ws.close(); } catch { /* noop */ }
            return;
          }
          handleEvent(data);
        } catch (err) {
          console.error('LiveNotifications WS parse error:', err);
        }
      };

      ws.onclose = (evt) => {
        if (cancelled || wsRef.current !== ws) return;
        wsHealthRef.current.connected = false;
        if (evt && evt.code === 4401) {
          terminalAuthFailureRef.current = true;
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
          console.error('LiveNotifications WS closed with 4401 (auth failure). Not reconnecting.');
          return;
        }
        if (cancelled) return;
        if (terminalAuthFailureRef.current) return;
        // P5 — exp backoff + ±50% jitter, capped at 30s
        const attempt = attemptRef.current || 0;
        const base = Math.min(30000, 1000 * Math.pow(2, attempt));
        const delay = Math.floor(base * (0.5 + Math.random()));
        attemptRef.current = attempt + 1;
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        if (cancelled || wsRef.current !== ws) return;
        wsHealthRef.current.connected = false;
        try { ws.close(); } catch { /* noop */ }
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      if (wsRef.current) {
        disposeWebSocket(wsRef.current);
        wsRef.current = null;
      }
      wsHealthRef.current.connected = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdForWs, handleEvent]);

  // Effect B — diff owned bot ids vs what we've subscribed to, and send
  // live_subscribe/live_unsubscribe deltas without re-opening the socket (P4).
  useEffect(() => {
    const next = (ownedBotIdsKey ? ownedBotIdsKey.split('|').filter(Boolean) : []);
    const prev = subscribedBotIdsRef.current || [];
    const added = next.filter((b) => !prev.includes(b));
    const removed = prev.filter((b) => !next.includes(b));
    if (added.length === 0 && removed.length === 0) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        if (added.length > 0) {
          ws.send(JSON.stringify({ type: 'live_subscribe', bot_ids: added }));
        }
        if (removed.length > 0) {
          ws.send(JSON.stringify({ type: 'live_unsubscribe', bot_ids: removed }));
        }
        subscribedBotIdsRef.current = next;
      } catch (err) {
        console.error('LiveNotifications WS sub-diff send error:', err);
      }
    } else {
      for (const b of added) {
        pendingSubsRef.current.add.add(b);
        pendingSubsRef.current.remove.delete(b);
      }
      for (const b of removed) {
        pendingSubsRef.current.remove.add(b);
        pendingSubsRef.current.add.delete(b);
      }
    }
  }, [ownedBotIdsKey]);

  // Initial bot fetch on mount
  useEffect(() => {
    fetchOwnedBots();
  }, [fetchOwnedBots]);

  // Hydrate joinedSessionsRef from the backend whenever the owned-bot set
  // changes. This keeps "new visitor message" toasts working after a full
  // page reload (when LiveInbox hasn't yet had a chance to register).
  // P2: uses the batch endpoint via reconcileJoinedSessions.
  // P3: also runs on a 60s interval + on WS onopen (see lifecycle effect).
  useEffect(() => {
    const myUserId = localStorage.getItem('user_id');
    if (!myUserId || !ownedBotIdsKey) return;
    let cancelled = false;

    (async () => {
      await reconcileJoinedSessions();
      if (cancelled) return;
    })();

    // P3 — periodic reconcile (60s). Cleared on deps change / unmount.
    clearInterval(reconcileTimerRef.current);
    reconcileTimerRef.current = setInterval(() => {
      reconcileJoinedSessions();
    }, 60000);

    return () => {
      cancelled = true;
      clearInterval(reconcileTimerRef.current);
      reconcileTimerRef.current = null;
    };
  }, [ownedBotIdsKey, reconcileJoinedSessions]);

  // Hydrate per-bot in-app notification preferences from the batch endpoint
  // GET /notification_settings?bot_ids=a,b,c → { "<bot_id>": { prefs, emails_by_section }, ... }
  // Cap 50 ids per call. Only the in_app channel is considered here — email/whatsapp
  // dispatch is gated server-side. (P2)
  const refreshInAppPrefs = useCallback(async (botIdsArg) => {
    const botIds = (botIdsArg && botIdsArg.length)
      ? botIdsArg
      : (ownedBotIdsKey ? ownedBotIdsKey.split('|').filter(Boolean) : []);
    if (!botIds.length) return;
    const CHUNK = 50;
    const chunks = [];
    for (let i = 0; i < botIds.length; i += CHUNK) {
      chunks.push(botIds.slice(i, i + CHUNK));
    }
    const merged = {};
    await Promise.all(chunks.map(async (ids) => {
      try {
        const qs = encodeURIComponent(ids.join(','));
        const res = await authFetch(`${API}notification_settings?bot_ids=${qs}`);
        if (!res || !res.ok) return;
        const data = await res.json();
        if (data && typeof data === 'object') {
          Object.assign(merged, data);
        }
      } catch {
        // Ignore — defaults will apply.
      }
    }));
    for (const botId of botIds) {
      const entry = merged[botId];
      const prefs = entry?.prefs || {};
      inAppPrefsRef.current.set(botId, {
        new_chat:       typeof prefs?.new_chat?.in_app       === 'boolean' ? prefs.new_chat.in_app       : true,
        help_requested: typeof prefs?.help_requested?.in_app === 'boolean' ? prefs.help_requested.in_app : true,
        new_message:    typeof prefs?.new_message?.in_app    === 'boolean' ? prefs.new_message.in_app    : true,
        new_lead:       typeof prefs?.new_lead?.in_app       === 'boolean' ? prefs.new_lead.in_app       : true,
        usage_warning:  typeof prefs?.usage_warning?.in_app  === 'boolean' ? prefs.usage_warning.in_app  : true,
      });
    }
  }, [ownedBotIdsKey]);

  useEffect(() => {
    if (!ownedBotIdsKey) return;
    let cancelled = false;
    (async () => {
      const botIds = ownedBotIdsKey.split('|').filter(Boolean);
      // Reset map on full hydrate so removed bots drop out.
      inAppPrefsRef.current = new Map();
      await refreshInAppPrefs(botIds);
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [ownedBotIdsKey, refreshInAppPrefs]);

  // Listen for in-app pref updates dispatched by LiveChatSettings save, so
  // toggles take effect immediately without a page reload.
  useEffect(() => {
    const handler = (e) => {
      const botId = e?.detail?.botId;
      if (!botId) return;
      refreshInAppPrefs([botId]);
    };
    window.addEventListener('live-chat-prefs-updated', handler);
    return () => window.removeEventListener('live-chat-prefs-updated', handler);
  }, [refreshInAppPrefs]);

  const handleOpen = useCallback((toast) => {
    if (toast?.botId) {
      // Pre-select the relevant bot in LiveChat (it reads from localStorage)
      localStorage.setItem('selectedBotId', toast.botId);
    }
    if (toast?.key) dismissToast(toast.key);
    // Toasts that are not conversation-shaped (e.g. usage_warning) carry
    // their own target route instead of the live inbox.
    if (toast?.href) {
      navigate(toast.href);
      return;
    }
    // If the agent is already on the Live page, skip the navigate() so React
    // Router doesn't remount LiveInbox. Still dispatch an event so the page
    // can react: SelectedBotContext switches the bot filter and LiveInbox
    // opens the conversation (`sessionId`).
    if (location.pathname === '/live') {
      try {
        window.dispatchEvent(new CustomEvent('live-notify-open', {
          detail: { botId: toast?.botId, sessionId: toast?.sessionId },
        }));
      } catch { /* noop */ }
      return;
    }
    // Same deep link the outbound channels (Telegram / Slack / Teams / email)
    // use: LiveChat selects the bot and opens the conversation on arrival.
    navigate(buildLiveDeepLink({ botId: toast?.botId, sessionId: toast?.sessionId }));
  }, [dismissToast, navigate, location.pathname]);

  // P7 — agent-online heartbeat
  const sendHeartbeats = useCallback(async () => {
    const bots = Array.from(onlineBotsRef.current);
    if (bots.length === 0) return;
    await Promise.all(bots.map(async (botId) => {
      try {
        const res = await authFetch(`${API}live/agent_heartbeat`, {
          method: 'POST',
          body: JSON.stringify({ bot_id: botId }),
        });
        // Self-heal: if the bot was deleted (or ownership changed) the
        // backend returns 404 / 403. Drop it from the ticker so we stop
        // hammering the endpoint forever.
        if (res && (res.status === 404 || res.status === 403)) {
          onlineBotsRef.current.delete(botId);
        }
      } catch (err) {
        // Best-effort — next tick will try again.
      }
    }));
    if (onlineBotsRef.current.size === 0 && heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const setAgentOnline = useCallback((botId, online) => {
    if (!botId) return;
    if (online) {
      onlineBotsRef.current.add(botId);
      // Fire one immediately so the server flips us to online without waiting
      // for the next interval tick.
      (async () => {
        try {
          await authFetch(`${API}live/agent_heartbeat`, {
            method: 'POST',
            body: JSON.stringify({ bot_id: botId }),
          });
        } catch { /* noop */ }
      })();
      // Start the interval if not already running
      if (!heartbeatTimerRef.current) {
        heartbeatTimerRef.current = setInterval(sendHeartbeats, 30000);
      }
    } else {
      onlineBotsRef.current.delete(botId);
      if (onlineBotsRef.current.size === 0 && heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    }
  }, [sendHeartbeats]);

  // P7 — cleanup the heartbeat interval on unmount
  useEffect(() => {
    return () => {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      onlineBotsRef.current.clear();
      // Also clear any outstanding toast safety timers so we don't leak them.
      for (const timer of toastSafetyTimersRef.current.values()) {
        clearTimeout(timer);
      }
      toastSafetyTimersRef.current.clear();
    };
  }, []);

  const ctxValue = useMemo(
    () => ({ registerJoinedSession, unregisterJoinedSession, setAgentOnline }),
    [registerJoinedSession, unregisterJoinedSession, setAgentOnline]
  );

  // Approximate height of one toast + gap. Used to vertically stack the
  // Snackbars at the same anchor (top-right). MUI doesn't stack these for
  // us, so we offset each one by index * STEP.
  const TOAST_STEP_PX = 120;

  return (
    <LiveNotificationsContext.Provider value={ctxValue}>
      {children}
      {/* Keyframe for the circular countdown indicator. Driven purely by
          CSS so it stays smooth regardless of React render cadence. */}
      <style>{`
        @keyframes ultimo-toast-countdown {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: 75.4; }
        }
      `}</style>
      {toasts.map((toast, idx) => (
        <Snackbar
          key={toast.key}
          open={!toast.exiting}
          autoHideDuration={TOAST_AUTO_HIDE_MS}
          // Don't pause the autoHide timer when the window loses focus —
          // otherwise toasts can persist forever if the agent switches tabs
          // before the timer expires.
          disableWindowBlurListener
          // Resume the timer immediately (with the full duration) once the
          // user moves the mouse away, instead of waiting indefinitely.
          resumeHideDuration={0}
          onClose={(_e, reason) => {
            if (reason === 'clickaway') return;
            dismissToast(toast.key);
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          TransitionComponent={SlideLeft}
          // Remove the toast from the array only after the slide-out
          // transition has played, so the exit animation is visible.
          TransitionProps={{ onExited: () => removeToast(toast.key) }}
          sx={{ mt: `${48 + idx * TOAST_STEP_PX}px`, mr: 2 }}
        >
          <Alert
            severity={toast.severity || 'info'}
            variant="filled"
            icon={false}
            sx={{
              width: 380,
              alignItems: 'flex-start',
              boxShadow: 4,
              borderRadius: '8px',
              backgroundColor: '#5616ea',
              color: '#fff',
              p: 2,
              py: 1.5,
              '& .MuiAlert-message': { width: '100%', p: 0 },
            }}
          >
            {/* Circular countdown indicator — sized & coloured to match the
                close button (X) sitting next to it on the top-right. */}
            <svg
              width={14}
              height={14}
              viewBox="0 0 28 28"
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 12,
                right: 32,
                pointerEvents: 'none',
                transform: 'rotate(-90deg)',
                color: '#fff',
                opacity: 0.8,
              }}
            >
              <circle
                cx="14"
                cy="14"
                r="12"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.3"
                strokeWidth="3"
              />
              <circle
                cx="14"
                cy="14"
                r="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="75.4"
                strokeDashoffset="0"
                style={{
                  animation: `ultimo-toast-countdown ${TOAST_AUTO_HIDE_MS}ms linear forwards`,
                }}
              />
            </svg>
            <IconButton
              size="small"
              onClick={() => dismissToast(toast.key)}
              aria-label={t('live_notify_dismiss') || 'Close'}
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                color: '#fff',
                opacity: 0.8,
                '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.12)' },
              }}
            >
              <FontAwesomeIcon icon={faXmark} style={{ fontSize: 14 }} />
            </IconButton>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{toast.title}</div>
            <div style={{ fontSize: 13, opacity: 0.95, marginBottom: 8 }}>{toast.body}</div>
            <Button
              size="small"
              onClick={() => handleOpen(toast)}
              startIcon={<FontAwesomeIcon icon={faUpRightFromSquare} style={{ fontSize: 12 }} />}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                color: '#5616ea',
                backgroundColor: '#fff',
                px: 1.5,
                py: 0.25,
                minWidth: 0,
                '&:hover': { backgroundColor: '#f3f0ff' },
              }}
            >
              {t('live_notify_open') || 'Open'}
            </Button>
          </Alert>
        </Snackbar>
      ))}
    </LiveNotificationsContext.Provider>
  );
};

export default LiveNotificationsProvider;
